import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import styles from './LoginPage.module.css';
import logo from '../../assets/logo.png';

const STEP_LOGIN = 'LOGIN';
const STEP_SEND_OTP = 'SEND_OTP';
const STEP_VERIFY_OTP = 'VERIFY_OTP';
const STEP_CHANGE_PASSWORD = 'CHANGE_PASSWORD';
const STEP_SUCCESS = 'SUCCESS';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(STEP_LOGIN);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() =>
    searchParams.get('expired') === 'true' ? 'Session expired. Please log in again.' : ''
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef([]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const clearError = () => setError('');

  // Handle Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearError();
    try {
      const res = await api.login({ email, password });
      if (res.success) {
        login(res.data, res.data.token);
        navigate('/');
      } else {
        setError(res.message);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Forgot Password click — starts the OTP flow manually, any time (not just first login)
  const handleForgotPasswordClick = () => {
    clearError();
    if (!email) {
      setError('Please enter your email address above, then click "Forgot password?".');
      return;
    }
    setStep(STEP_SEND_OTP);
  };

  // Handle Send OTP
  const handleSendOtp = async () => {
    setLoading(true);
    clearError();
    try {
      const res = await api.sendOtp({ email, purpose: 'CHANGE_PASSWORD' });
      if (res.success) {
        setStep(STEP_VERIFY_OTP);
      } else {
        setError(res.message);
      }
    } catch {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP Input
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setOtp(paste.split(''));
    }
  };

  // Handle Verify OTP
  const handleVerifyOtp = async () => {
    const otpString = otp.join('');
    if (otpString.length < 6) {
      setError('Please enter the complete 6-digit OTP.');
      return;
    }
    setLoading(true);
    clearError();
    try {
      const res = await api.verifyOtp({
        email,
        otp: otpString,
        purpose: 'CHANGE_PASSWORD',
      });
      if (res.success) {
        setStep(STEP_CHANGE_PASSWORD);
      } else {
        setError(res.message);
      }
    } catch {
      setError('Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Change Password
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    clearError();
    try {
      const res = await api.changePassword({
        email,
        otp: otp.join(''),
        newPassword,
        confirmPassword,
      });
      if (res.success) {
        // change-password doesn't return a token — sign in fresh with the new password.
        const loginRes = await api.login({ email, password: newPassword });
        if (loginRes.success) {
          setStep(STEP_SUCCESS);
          setTimeout(() => {
            login(loginRes.data, loginRes.data.token);
            navigate('/');
          }, 2000);
        } else {
          setStep(STEP_LOGIN);
          setPassword('');
          setError('Password changed successfully. Please sign in with your new password.');
        }
      } else {
        setError(res.message);
      }
    } catch {
      setError('Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>

        {/* Logo */}
        <div className={styles.logoWrapper}>
          <img src={logo} alt="Density Pharmachem" className={styles.logo} />
        </div>

        {/* STEP: LOGIN */}
        {step === STEP_LOGIN && (
          <>
            <div className={styles.title}>Welcome Back</div>
            <div className={styles.desc}>Sign in to your HR Portal account</div>
            {error && <div className={styles.error}>{error}</div>}
            <form onSubmit={handleLogin}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Email Address</label>
                <div className={styles.inputWrapper}>
                  <input
                    className={styles.input}
                    type="email"
                    placeholder="your@densitypharmachem.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <span className={styles.inputIcon}>✉</span>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Password</label>
                <div className={styles.inputWrapper}>
                  <input
                    className={styles.input}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <span
                    className={styles.inputIcon}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '🙈' : '👁'}
                  </span>
                </div>
              </div>
              <div className={styles.forgotPasswordRow}>
                <button
                  type="button"
                  className={styles.resendLink}
                  onClick={handleForgotPasswordClick}
                >
                  Forgot password?
                </button>
              </div>
              <button className={styles.button} type="submit" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </>
        )}

        {/* STEP: SEND OTP */}
        {step === STEP_SEND_OTP && (
          <>
            <button className={styles.backLink} onClick={() => setStep(STEP_LOGIN)}>
              ← Back
            </button>
            <div className={styles.otpTitle}>Reset Your Password</div>
            <div className={styles.otpDesc}>
              We will send an OTP to <strong>{email}</strong> to reset your password.
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <button className={styles.button} onClick={handleSendOtp} disabled={loading}>
              {loading ? 'Sending OTP...' : 'Send OTP to my Email'}
            </button>
          </>
        )}

        {/* STEP: VERIFY OTP */}
        {step === STEP_VERIFY_OTP && (
          <>
            <button className={styles.backLink} onClick={() => setStep(STEP_SEND_OTP)}>
              ← Back
            </button>
            <div className={styles.otpTitle}>Enter OTP</div>
            <div className={styles.otpDesc}>
              Enter the 6-digit OTP sent to <strong>{email}</strong>
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.otpInputRow} onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  className={styles.otpBox}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                />
              ))}
            </div>
            <div className={styles.resend}>
              Didn't receive?{' '}
              <button className={styles.resendLink} onClick={handleSendOtp}>
                Resend OTP
              </button>
            </div>
            <button className={styles.button} onClick={handleVerifyOtp} disabled={loading}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </>
        )}

        {/* STEP: CHANGE PASSWORD */}
        {step === STEP_CHANGE_PASSWORD && (
          <>
            <button className={styles.backLink} onClick={() => setStep(STEP_VERIFY_OTP)}>
              ← Back
            </button>
            <div className={styles.otpTitle}>Set New Password</div>
            <div className={styles.otpDesc}>
              Choose a strong password for your account.
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.formGroup}>
              <label className={styles.label}>New Password</label>
              <div className={styles.inputWrapper}>
                <input
                  className={styles.input}
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <span
                  className={styles.inputIcon}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? '🙈' : '👁'}
                </span>
              </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Confirm Password</label>
              <div className={styles.inputWrapper}>
                <input
                  className={styles.input}
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <span
                  className={styles.inputIcon}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? '🙈' : '👁'}
                </span>
              </div>
            </div>
            <button className={styles.greenButton} onClick={handleChangePassword} disabled={loading}>
              {loading ? 'Saving...' : 'Set New Password'}
            </button>
          </>
        )}

        {/* STEP: SUCCESS */}
        {step === STEP_SUCCESS && (
          <>
            <div className={styles.successIcon}>✅</div>
            <div className={styles.otpTitle}>Password Set Successfully!</div>
            <div className={styles.otpDesc}>
              Redirecting you to the dashboard...
            </div>
          </>
        )}

        <div className={styles.footer}>
          © {new Date().getFullYear()} Density Pharmachem. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default LoginPage;